import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/auth";
import { EmployeeStatus, ResidencyType, WorkerType } from "@/contexts/employees/EmployeesContext";
import { UserRole, OvertimeStatus } from "@/types/enums";
import { ShiftService } from "@/services/shiftService";
import { HolidayService } from "@/services/holidayService";
// logger removed

/**
 * Common utility function for chunked data fetching to bypass Supabase's 1000 row limit
 * @param baseQuery - The base Supabase query to execute
 * @param chunkSize - Size of each chunk (default: 1000)
 * @returns Promise with all data and total count
 */
async function fetchDataInChunks<T>(
  baseQuery: any,
  chunkSize: number = 1000
): Promise<{ data: T[]; totalCount: number }> {
  const allData: T[] = [];
  let offset = 0;
  
  while (true) {
    const chunkQuery = baseQuery.range(offset, offset + chunkSize - 1);
    const { data: chunkData, error: chunkError } = await chunkQuery;
    
    if (chunkError) {
      throw new Error(`Failed to fetch data: ${chunkError.message}`);
    }
    
    if (!chunkData || chunkData.length === 0) {
      break; // No more data to fetch
    }
    
    allData.push(...chunkData);
    
    if (chunkData.length < chunkSize) {
      break; // Last chunk, we've got all data
    }
    
    offset += chunkSize;
  }
  
  return {
    data: allData,
    totalCount: allData.length
  };
}

/**
 * Hook for fetching daily overtime records using the get_daily_overtime_records edge function
 * This hook properly caches the data for efficient access across the app
 */
/**
 * Hook for fetching daily overtime records
 * Now uses the UUID relationship between employees and overtime_requests
 */
export function useDailyOvertimeRecords({
  companyId,
  department,
  enabled = true,
}: {
  companyId: string;
  department?: string | string[];
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ['daily_overtime_records', companyId, department],
    queryFn: async () => {
      
      
      if (!companyId) {
        throw new Error('Company ID is required');
      }
      
      let query = supabase
        .from('overtime_requests')
          .select(`
            id, 
            date, 
            hours, 
            status, 
            employee_id,
            employee_name,
            department,
            approver_name,
            created_by_name,
            submitted_at,
            justification,
            overtime_reasons!justification(
              main_title,
              reason
            ),
            employees!overtime_requests_employee_id_fkey(
              company_id,
              employee_id
            )
          `)
          .eq('employees.company_id', companyId);
        
        if (department) {
          if (Array.isArray(department)) {
            if (department.length > 0) {
              query = query.in('department', department);
            }
          } else {
            query = query.eq('department', department);
          }
        }
        
        // Fetch all data in chunks to bypass Supabase's 1000 row limit
        const { data, totalCount } = await fetchDataInChunks<any>(
          query.order('date', { ascending: false })
        );
        
        // Error handling is now done within the chunk fetching loop
        
        if (!data || data.length === 0) {
          return { data: {} };
        }
        
        interface DailyOvertimeRecordEntry {
          date: string; 
          employee_id: string; 
          employee_name: string;
          department: string;
          hours: number;
          status: string;
          approver_name: string;
          created_by_name: string;
          submitted_at: string | null;
          employee_display_id?: string; // Human-readable employee ID from employees table
          justification: string | null;
          reason_category: string | null;
          reason: string | null;
        }

        const dailyRecords: { [date: string]: DailyOvertimeRecordEntry[] } = {};
        
        data.forEach((record: any) => {
          const date = record.date;
          if (!dailyRecords[date]) {
            dailyRecords[date] = [];
          }
          
          const employeeName = record.employee_name || 'Unknown';
          
          // Extract employee_id from the employees relation
          let employeeDisplayId = 'Unknown';
          if (record.employees && typeof record.employees === 'object' && record.employees !== null) {
            const employeeData = record.employees as { employee_id?: string };
            employeeDisplayId = employeeData.employee_id || 'Unknown';
          }
          
          // Extract reason information from overtime_reasons relation
          let reasonCategory = null;
          let reason = null;
          if (record.overtime_reasons) {
            if (Array.isArray(record.overtime_reasons) && record.overtime_reasons.length > 0) {
              // Handle array format (inner join)
              reasonCategory = record.overtime_reasons[0].main_title || null;
              reason = record.overtime_reasons[0].reason || null;
            } else if (typeof record.overtime_reasons === 'object' && record.overtime_reasons !== null) {
              // Handle object format (regular join)
              reasonCategory = record.overtime_reasons.main_title || null;
              reason = record.overtime_reasons.reason || null;
            }
          }
          
          dailyRecords[date].push({
            date: record.date,
            employee_id: record.employee_id,
            employee_name: employeeName,
            department: record.department || 'Unknown',
            hours: record.hours || 0,
            status: record.status || OvertimeStatus.PENDING,
            approver_name: record.approver_name || '',
            created_by_name: record.created_by_name || '',
            submitted_at: record.submitted_at || null,
            employee_display_id: employeeDisplayId,
            justification: record.justification || null,
            reason_category: reasonCategory,
            reason: reason
          });
        });
        
        return { data: dailyRecords };
    },
    enabled: enabled && !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching employees from the database
 * Supports pagination and filtering by department
 */
export function useEmployees({
  department,
  page = 0,
  pageSize = 10,
  enabled = true,
  sortField = 'name',
  sortDirection = 'asc',
}: {
  department?: string | string[];
  page?: number;
  pageSize?: number;
  enabled?: boolean;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}) {
  const mapSortFieldToDb = (field: string): string => {
    const MAPPINGS: { [key: string]: string } = {
      firstName: 'first_name',
      lastName: 'last_name',
      email: 'email',
      role: 'role',
      department: 'department',
      position: 'position',
      status: 'status',
      shift: 'shift',
      residency: 'residency',
      workerType: 'worker_type'
    };
    return MAPPINGS[field] || field;
  };

  return useQuery({
    queryKey: ['employees', department, page, pageSize, sortField, sortDirection],
    queryFn: async () => {
      
      
      let query = supabase
        .from('employees')
          .select('*', { count: 'exact' });

        // Apply server-side sorting
        if (sortField === 'name') {
          query = query
            .order('first_name', { ascending: sortDirection === 'asc' })
            .order('last_name', { ascending: sortDirection === 'asc' });
        } else {
          const dbSortField = mapSortFieldToDb(sortField);
          query = query.order(dbSortField, { ascending: sortDirection === 'asc' });
        }
        
        // Apply department filter if provided
        if (department) {
          if (Array.isArray(department)) {
            if (department.length > 0) {
              query = query.in('department', department);
            }
          } else {
            query = query.eq('department', department);
          }
        }
        
        // If pageSize is very large (e.g., 10000), fetch all data in chunks to bypass Supabase's 1000 row limit
        let allData: any[] = [];
        let totalCount = 0;
        
        if (pageSize >= 1000) {
          // Use common chunked fetching function
          const result = await fetchDataInChunks<any>(query);
          allData = result.data;
          totalCount = result.totalCount;
        } else {
          // Apply normal pagination for smaller page sizes
          const from = page * pageSize;
          const to = from + pageSize - 1;
          query = query.range(from, to);
          
          const { data: paginatedData, error: paginationError, count: exactCount } = await query;
          
          if (paginationError) {
            throw new Error(`Failed to fetch employees: ${paginationError.message}`);
          }
          
          allData = paginatedData || [];
          totalCount = exactCount || 0;
        }

        const data = allData;
        const count = totalCount;
        
        // Error handling is now done within the chunk fetching logic
        
        // Transform the data to match the User type
        const employees = data.map(emp => ({
          id: emp.id,
          email: emp.email || '',
          role: emp.role || UserRole.EMPLOYEE,
          firstName: emp.first_name || '',
          lastName: emp.last_name || '',
          name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
          department: emp.department || '',
          position: emp.position || '',
          companyId: emp.company_id,
          status: emp.status || EmployeeStatus.ACTIVE,
          shift: emp.shift || '',
          residency: emp.residency || ResidencyType.RESIDENT,
          workerType: emp.worker_type || WorkerType.FULL_TIME,
          employee_id: emp.employee_id // Add employee_id from database
        }));
        
        return {
          employees,
          totalCount: count || 0,
          page,
          pageSize,
          totalPages: count ? Math.ceil(count / pageSize) : 0
        };
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching ALL employees from the database at once
 * Used for client-side pagination with DataTable component (similar to Audit page)
 */
export function useAllEmployees({
  department,
  enabled = true,
}: {
  department?: string | string[];
  enabled?: boolean;
}) {
  const { user } = useAuth();
  const companyId = user?.companyId;

  return useQuery({
    queryKey: ['all_employees', companyId, department],
    queryFn: async () => {
      
      
      if (!companyId) {
        throw new Error('Company ID is required');
      }
      
      let query = supabase
        .from('employees')
          .select('*')
          .eq('company_id', companyId);
        
        // Apply department filter if provided
        if (department) {
          if (Array.isArray(department)) {
            if (department.length > 0) {
              query = query.in('department', department);
            }
          } else {
            query = query.eq('department', department);
          }
        }
        
        // Order by name for consistent display
        query = query
          .order('first_name', { ascending: true })
          .order('last_name', { ascending: true });
        
        // Use common chunked fetching function to bypass Supabase's 1000 row limit
        const { data, totalCount } = await fetchDataInChunks<any>(query);
        
        // Transform the data to match the User type (moved inside queryFn for efficiency)
        const employees = data.map(emp => ({
          id: emp.id,
          email: emp.email || '',
          role: emp.role || UserRole.EMPLOYEE,
          firstName: emp.first_name || '',
          lastName: emp.last_name || '',
          name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
          department: emp.department || '',
          position: emp.position || '',
          companyId: emp.company_id,
          status: emp.status || EmployeeStatus.ACTIVE,
          shift: emp.shift || '',
          residency: emp.residency || ResidencyType.RESIDENT,
          workerType: emp.worker_type || WorkerType.FULL_TIME,
          employee_id: emp.employee_id || ''
        }));
        return employees;
    },
    enabled: enabled && !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching overtime requests
 * Used by analytics and department dashboards
 */
/**
 * Hook for infinite loading of employees
 * Used by the EmployeesContext for efficient pagination
 */
export function useInfiniteEmployees({
  department,
  pageSize = 50,
  enabled = true,
  sortField = 'name',
  sortDirection = 'asc',
}: {
  department?: string | string[];
  pageSize?: number;
  enabled?: boolean;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}) {
  // Define the return type for the query function for better type safety
  type EmployeesPage = {
    employees: Array<{
      id: string;
      email: string;
      role: string;
      firstName: string;
      lastName: string;
      name: string;
      department: string;
      position: string;
      companyId: string;
      status: string;
      shift: string;
      residency: string;
      workerType: string;
      employee_id: string; // Include employee_id in type definition
    }>;
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };

  const mapSortFieldToDb = (field: string): string => {
    const MAPPINGS: { [key: string]: string } = {
      // name: 'name', // Handled specially for multi-column sort
      firstName: 'first_name',
      lastName: 'last_name',
      email: 'email',
      role: 'role',
      department: 'department',
      position: 'position',
      status: 'status',
      shift: 'shift',
      residency: 'residency',
      workerType: 'worker_type'
    };
    return MAPPINGS[field] || field;
  };

  return useInfiniteQuery<EmployeesPage, Error>({
    queryKey: ['employees_infinite', department, pageSize, sortField, sortDirection],
    queryFn: async ({ pageParam }) => {
      
      
      try {
        let query = supabase
          .from('employees')
          .select('*', { count: 'exact' });

        // Apply server-side sorting
        if (sortField === 'name') {
          query = query
            .order('first_name', { ascending: sortDirection === 'asc' })
            .order('last_name', { ascending: sortDirection === 'asc' });
        } else {
          const dbSortField = mapSortFieldToDb(sortField);
          query = query.order(dbSortField, { ascending: sortDirection === 'asc' });
        }
        
        // Apply department filter if provided
        if (department) {
          if (Array.isArray(department)) {
            if (department.length > 0) {
              query = query.in('department', department);
            }
          } else {
            query = query.eq('department', department);
          }
        }
        
        // Apply pagination
        const from = Number(pageParam) * pageSize;
        const to = from + pageSize - 1;
        query = query.range(from, to);
        
        const { data, error, count } = await query;
        
        if (error) {
          throw new Error(`Failed to fetch employees: ${error.message}`);
        }
        
        const employees = data.map((emp_db: any) => {
          const constructedName = `${emp_db.first_name || ''} ${emp_db.last_name || ''}`.trim();
          return {
            id: emp_db.id,
            email: emp_db.email || '',
            role: emp_db.role || UserRole.EMPLOYEE,
            firstName: emp_db.first_name || '',
            lastName: emp_db.last_name || '',
            name: constructedName, // Use only the constructed name
            department: emp_db.department || '',
            position: emp_db.position || '',
            companyId: emp_db.company_id,
            status: emp_db.status || EmployeeStatus.ACTIVE,
            shift: emp_db.shift || '',
            residency: emp_db.residency || ResidencyType.RESIDENT,
            workerType: emp_db.worker_type || WorkerType.FULL_TIME,
            employee_id: emp_db.employee_id || '' // Add employee_id from database, fallback to empty string
          };
        });
        
        return {
          employees,
          totalCount: count || 0,
          page: Number(pageParam),
          pageSize,
          totalPages: count ? Math.ceil(count / pageSize) : 0
        };
      } catch {
        // Re-throw the error
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      return nextPage < lastPage.totalPages ? nextPage : undefined;
    },
    enabled,
    staleTime: 15 * 60 * 1000, // 15 minutes - increase cache time to improve performance
    refetchOnWindowFocus: false,
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection time
  });
}

export function useSearchableInfiniteEmployees({
  department,
  searchQuery = '',
  pageSize = 50,
  enabled = true,
  sortField = 'name',
  sortDirection = 'asc',
}: {
  department?: string | string[];
  searchQuery?: string;
  pageSize?: number;
  enabled?: boolean;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}) {
  // Define the return type for the query function for better type safety
  type EmployeesPage = {
    employees: Array<{
      id: string;
      email: string;
      role: UserRole; // Updated to use UserRole type
      firstName: string;
      lastName: string;
      name: string;
      department: string;
      position: string;
      companyId: string;
      status: string;
      shift: string;
      residency: string;
      workerType: string;
      employee_id: string;
    }>;
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };

  const mapSortFieldToDb = (field: string): string => {
    const MAPPINGS: { [key: string]: string } = {
      // name: 'name', // Handled specially for multi-column sort
      firstName: 'first_name',
      lastName: 'last_name',
      email: 'email',
      role: 'role',
      department: 'department',
      position: 'position',
      status: 'status',
      shift: 'shift',
      residency: 'residency',
      workerType: 'worker_type'
    };
    return MAPPINGS[field] || field;
  };

  return useInfiniteQuery<EmployeesPage, Error>({
    queryKey: ['employees_searchable', department, searchQuery, pageSize, sortField, sortDirection],
    queryFn: async ({ pageParam }) => {
      
      
      try {
        let query = supabase
          .from('employees')
          .select('*, employee_id', { count: 'exact' });

        // Apply search filter if provided
        if (searchQuery && searchQuery.trim() !== '') {
          const searchTerm = `%${searchQuery.trim().toLowerCase()}%`;
          query = query.or(
            `first_name.ilike.${searchTerm},last_name.ilike.${searchTerm},email.ilike.${searchTerm},employee_id.ilike.${searchTerm}`
          );
        }

        // Apply server-side sorting
        if (sortField === 'name') {
          query = query
            .order('first_name', { ascending: sortDirection === 'asc' })
            .order('last_name', { ascending: sortDirection === 'asc' });
        } else {
          const dbSortField = mapSortFieldToDb(sortField);
          query = query.order(dbSortField, { ascending: sortDirection === 'asc' });
        }
        
        // Apply department filter if provided
        if (department) {
          if (Array.isArray(department)) {
            if (department.length > 0) {
              query = query.in('department', department);
            }
          } else {
            query = query.eq('department', department);
          }
        }
        
        // Apply pagination
        const from = Number(pageParam) * pageSize;
        const to = from + pageSize - 1;
        query = query.range(from, to);
        
        const { data, error, count } = await query;
        
        if (error) {
          throw new Error(`Failed to fetch employees: ${error.message}`);
        }
        
        const employees = data.map((emp_db: any) => {
          const constructedName = `${emp_db.first_name || ''} ${emp_db.last_name || ''}`.trim();
          // Ensure role is a valid UserRole
          const role = (emp_db.role || UserRole.EMPLOYEE) as UserRole;
          
          return {
            id: emp_db.id,
            email: emp_db.email || '',
            role, // Use the typed role
            firstName: emp_db.first_name || '',
            lastName: emp_db.last_name || '',
            name: constructedName,
            department: emp_db.department || '',
            position: emp_db.position || '',
            companyId: emp_db.company_id,
            status: emp_db.status || EmployeeStatus.ACTIVE,
            shift: emp_db.shift || '',
            residency: emp_db.residency || ResidencyType.RESIDENT,
            workerType: emp_db.worker_type || WorkerType.FULL_TIME,
            employee_id: emp_db.employee_id || '',
          };
        });
        
        return {
          employees,
          totalCount: count || 0,
          page: Number(pageParam),
          pageSize,
          totalPages: count ? Math.ceil(count / pageSize) : 0
        };
      } catch {
        // Re-throw the error
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      return nextPage < lastPage.totalPages ? nextPage : undefined;
    },
    enabled,
    staleTime: 15 * 60 * 1000, // 15 minutes - increase cache time to improve performance
    refetchOnWindowFocus: false,
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection time
  });
}

// Define a reason structure
interface OvertimeReason {
  main_title: string;
  reason: string | null;
}

export function useOvertimeRequests({
  status,
  department,
  employeeId,
  companyId,
  startDate,
  endDate,
  page = 0,
  pageSize = 10,
  enabled = true,
}: {
  status?: string;
  department?: string | string[];
  employeeId?: string;
  companyId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ['overtimeRequests', status, department, employeeId, companyId, startDate, endDate, page, pageSize],
    queryFn: async () => {
      
      try {
        let query = supabase
          .from('overtime_requests')
          .select(`
            *, 
            employees!overtime_requests_employee_id_fkey(first_name, last_name, department, company_id),
            overtime_reasons!justification(main_title, reason)
          `, { count: 'exact' });

        if (status) {
          query = query.eq('status', status);
        }

        // Apply companyId filter if provided, via the joined employees table
        if (companyId) {
          query = query.eq('employees.company_id', companyId);
        }

        // Apply department filter if provided
        // This should filter on the overtime_requests.department column
        if (department) {
          if (Array.isArray(department)) {
            if (department.length > 0) {
              query = query.in('department', department);
            }
            // If department is an empty array, no filter applied.
          } else {
            query = query.eq('department', department);
          }
        }

        if (employeeId) {
          query = query.eq('employee_id', employeeId);
        }

        if (startDate) {
          query = query.gte('date', startDate);
        }

        if (endDate) {
          query = query.lte('date', endDate);
        }

        // Order by date descending (newest first)
        query = query.order('date', { ascending: false });

        // If pageSize is very large (e.g., 10000), fetch all data in chunks to bypass Supabase's 1000 row limit
        let allData: any[] = [];
        let totalCount = 0;
        
        if (pageSize >= 1000) {
          // Use common chunked fetching function
          const result = await fetchDataInChunks<any>(query);
          allData = result.data;
          totalCount = result.totalCount;
        } else {
          // Apply normal pagination for smaller page sizes
          const from = page * pageSize;
          const to = from + pageSize - 1;
          query = query.range(from, to);
          
          const { data: paginatedData, error, count: exactCount } = await query;
          
          if (error) {
            throw new Error(`Failed to fetch overtime requests: ${error.message}`);
          }
          
          allData = paginatedData || [];
          totalCount = exactCount || 0;
        }

        const data = allData;
        const count = totalCount;

        // Error handling is now done within the chunk fetching logic

        // Transform data to include justification_text
        const transformedRequests = (data || []).map(request => ({
          ...request,
          justification_text: (() => {
            // Extract justification text - prioritize reason column
            if (request.overtime_reasons) {
              if (Array.isArray(request.overtime_reasons) && request.overtime_reasons.length > 0) {
                // Handle array format (inner join)
                return request.overtime_reasons[0].reason || request.overtime_reasons[0].main_title || 'Unknown reason';
              } else if (typeof request.overtime_reasons === 'object' && request.overtime_reasons !== null) {
                // Handle object format (regular join)
                const reasonObj = request.overtime_reasons as OvertimeReason;
                return reasonObj.reason || reasonObj.main_title || 'Unknown reason';
              }
            }
            return 'Unknown reason';
          })()
        }));

        return {
          requests: transformedRequests,
          totalCount: count || 0,
          page,
          pageSize,
          totalPages: count ? Math.ceil(count / pageSize) : 0
        };
      } catch {
        
        // Re-throw the error
      }
    },
    enabled,
    staleTime: 30 * 1000, // Consider data stale after 30 seconds
    refetchOnMount: 'always', // Always refetch when mounted
    refetchOnWindowFocus: true, // Refetch when window gains focus
    retry: 2 // Retry failed requests twice
  });
}

/**
 * Hook for fetching departments with caching
 * This prevents repeated API calls when rendering multiple DepartmentSelectors
 */
export function useDepartments() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['departments', user?.companyId],
    queryFn: async () => {
      
      
      try {
        let query = supabase.from('departments').select('name').eq('is_del', 'N');
        
        if (user?.companyId) {
          query = query.eq('company_id', user.companyId);
        }
        
        query = query.order('name');
        
        const { data, error } = await query;
        
        if (error) {
          throw error;
        }
        
        return data?.map((dept: any) => dept.name) || [];
      } catch {
        // Re-throw the error
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    gcTime: 15 * 60 * 1000, // 15 minutes garbage collection time (replaces cacheTime)
  });
}

/**
 * Hook to fetch shifts for a company
 */
export function useShifts(companyId: string) {
  return useQuery({
    queryKey: ['shifts', companyId],
    queryFn: () => ShiftService.fetchShifts(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    gcTime: 15 * 60 * 1000, // 15 minutes garbage collection time (replaces cacheTime)
  });
}

/**
 * Hook to fetch holidays for a company within a date range
 */
export function useHolidays(
  companyId: string,
  startDate: string,
  endDate: string
) {
  return useQuery({
    queryKey: ['holidays', companyId, startDate, endDate],
    queryFn: () => HolidayService.fetchHolidays(companyId, startDate, endDate),
    enabled: !!companyId && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to fetch holidays for a specific month
 */
export function useHolidaysForMonth(
  companyId: string,
  year: number,
  month: number
) {
  return useQuery({
    queryKey: ['holidays', companyId, year, month],
    queryFn: () => HolidayService.fetchHolidaysForMonth(companyId, year, month),
    enabled: !!companyId && !!year && !!month,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

// NEW: Duty Roster Hooks

/**
 * Hook to fetch employees by duty roster status
 */
export function useEmployeesByDutyRosterStatus(
  companyId: string,
  isDutyRoster?: boolean,
  includeInactive: boolean = false
) {
  return useQuery({
    queryKey: ['employeesByDutyRosterStatus', companyId, isDutyRoster, includeInactive],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_employees_by_duty_roster_status', {
        p_company_id: companyId,
        p_is_duty_roster: isDutyRoster || null,
        p_include_inactive: includeInactive
      });
      
      if (error) throw error;
      return data;
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch duty roster employees with their group information
 */
export function useDutyRosterEmployeesWithGroups(
  companyId: string,
  groupId?: string
) {
  return useQuery({
    queryKey: ['dutyRosterEmployeesWithGroups', companyId, groupId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_duty_roster_employees_with_groups', {
        p_company_id: companyId,
        p_group_id: groupId || null
      });
      
      if (error) throw error;
      return data;
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to validate employee-group residency compatibility
 */
export function useValidateEmployeeGroupResidency(
  employeeId: string,
  groupId: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['validateEmployeeGroupResidency', employeeId, groupId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('validate_employee_group_residency', {
        p_employee_id: employeeId,
        p_group_id: groupId
      });
      
      if (error) throw error;
      return data;
    },
    enabled: enabled && !!employeeId && !!groupId,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to validate employee duty roster assignment
 */
export function useValidateEmployeeDutyRosterAssignment(
  employeeId: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['validateEmployeeDutyRosterAssignment', employeeId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('validate_employee_duty_roster_assignment', {
        p_employee_id: employeeId
      });
      
      if (error) throw error;
      return data;
    },
    enabled: enabled && !!employeeId,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch duty roster groups for a company
 */
export function useDutyRosterGroups(companyId: string) {
  return useQuery({
    queryKey: ['dutyRosterGroups', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_groups')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('group_name', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to fetch duty roster groups filtered by residency type
 */
export function useDutyRosterGroupsByResidency(
  companyId: string,
  residencyType?: string
) {
  return useQuery({
    queryKey: ['dutyRosterGroupsByResidency', companyId, residencyType],
    queryFn: async () => {
      let query = supabase
        .from('emp_groups')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true);
      
      if (residencyType) {
        query = query.eq('residency_type', residencyType);
      }
      
      const { data, error } = await query.order('group_name', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to fetch employees in a specific duty roster group
 */
export function useEmployeesInGroup(groupId: string) {
  return useQuery({
    queryKey: ['employeesInGroup', groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employees')
        .select(`
          id,
          first_name,
          last_name,
          email,
          department,
          position,
          status,
          residency,
          group_id,
          is_duty_roster,
          created_at,
          updated_at
        `)
        .eq('group_id', groupId)
        .eq('is_duty_roster', true)
        .order('first_name', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!groupId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch ungrouped duty roster employees
 */
export function useUngroupedDutyRosterEmployees(companyId: string) {
  return useQuery({
    queryKey: ['ungroupedDutyRosterEmployees', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employees')
        .select(`
          id,
          first_name,
          last_name,
          email,
          department,
          position,
          status,
          residency,
          group_id,
          is_duty_roster,
          created_at,
          updated_at
        `)
        .eq('company_id', companyId)
        .eq('is_duty_roster', true)
        .is('group_id', null)
        .order('first_name', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}
